import React, { useEffect, useRef } from 'react';
import {
  Avatar,
  Badge,
  LogoWrapper,
  NavLinkIconWrapper,
  NavLinkText,
  NavigationContainer,
  NavigationItem,
  NavigationItems,
  NavigationWrapper,
  PostStoryBtn,
  RedDot,
  Row,
} from './Navigation.styles';
import { ReactComponent as HomeIcon } from '../../assets/icons/home.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/user.svg';
import { ReactComponent as WalletIcon } from '../../assets/icons/wallet-1.svg';
import { ReactComponent as UsersIcon } from '../../assets/icons/users.svg';
import { ReactComponent as SettingsIcon } from '../../assets/icons/settings.svg';
import { ReactComponent as ImageUserPlusIcon } from '../../assets/icons/image-user-plus.svg';
import { ReactComponent as MessageChatSquareIcon } from '../../assets/icons/message-chat-square.svg';
import { ReactComponent as BookClosedIcon } from '../../assets/icons/book-closed.svg';
import { ReactComponent as SafeIcon } from '../../assets/icons/safe1.svg';
import { ReactComponent as BellIcon } from '../../assets/icons/bell.svg';
import { ReactComponent as AddIcon } from '../../assets/icons/plus-circle-white.svg';
import { ReactComponent as UserEditIcon } from '../../assets/icons/user-edit.svg';
import { ReactComponent as ListIcon } from '../../assets/icons/list.svg';
import { ReactComponent as AnalyticsIcon } from '../../assets/icons/bar-chart.svg';
import { ReactComponent as AnnotationAlertIcon } from '../../assets/icons/annotation-alert.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AGENCY_HOME,
  AGENCY_INFLUENCERS,
  AGENCY_SETTINGS,
  CHAT,
  CHATTER_COLLECTIONS,
  CHATTER_HOME,
  ORGANIZATION_MEMBERS,
  CHATTER_MESSAGES,
  CHATTER_NOTIFICATIONS,
  CHATTER_EDIT,
  CHATTER_SETTINGS,
  CHATTER_VAULT,
  HOME,
  MESSAGES,
  PROFILE,
  REFERRALS,
  WALLET,
  ORGANIZATION_ANALYTICS,
  MESSAGE_REVIEWS,
} from '../App/routes';
import LogoComponent from '../LogoComponent/LogoComponent';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/user/slice';
import {
  CRM_ORGANIZATION_MEMBER_ROLE,
  CRM_ORGANIZATION_MEMBER_ROLES,
  SOCKET_EVENTS,
  USER_ROLE,
} from '../../utils/constants';
import { Text15Bold } from '../utils/Text/Text.styles';
import { selectHasNewNotifications, setHasNewNotifications } from '../../store/slices/collections/slice';
import { useSocketEvent } from '../../hooks/useSocketEvent';
import { selectChatterActionsPending, selectSelectedInfluencer } from '../../store/slices/chatter/slice';
import { fetchInfluencerById } from '../../store/slices/chatter/asyncThunks';
import { notifyInfo } from '../../utils/notify';
import { fetchInfluencerNotificationStatus } from '../../store/slices/collections/asyncThunks';
import { useInfluencerRoom } from '../../hooks/useInfluencerRoom';
import PostStoryModal from '../modals/PostStoryModal/PostStoryModal';
import { formatPrice } from '../../utils/util';

const Navigation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const newNotifications = useSelector(selectHasNewNotifications);
  const influencer = useSelector(selectSelectedInfluencer);
  const { fetchInfluencerById: fetchInfluencerByIdPending } = useSelector(selectChatterActionsPending);
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  const postStoryModalRef = useRef();
  const { influencerId } = useParams();
  const isOwnerOrTeamLead = [CRM_ORGANIZATION_MEMBER_ROLE.OWNER, CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR, CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD].includes(
    user.role,
  );
  const canViewMessages = [
    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
    CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
    CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
  ].includes(user.role);
  const canViewVault = [
    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
    CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
    CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
    CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
  ].includes(user.role);
  const canViewNotification = [
    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
    CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
    CRM_ORGANIZATION_MEMBER_ROLE.CHATTER,
  ].includes(user.role);
  const canEditInfluencer = [CRM_ORGANIZATION_MEMBER_ROLE.OWNER, CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR].includes(
    user.role,
  );
  const canPostStory = [
    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
    CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
  ].includes(user.role);
  const canViewMessageReviews = [
    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
    CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
  ].includes(user.role);
  useInfluencerRoom(influencerId);

  useEffect(() => {
    if (influencerId) {
      dispatch(fetchInfluencerNotificationStatus({ influencerId }));
      dispatch(fetchInfluencerById({ influencerId }));
    }
  }, [influencerId, dispatch]);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  const handleNotification = data => {
    if (canViewMessages) {
      if (!pathnameRef.current.includes('notifications')) {
        if (data?.target === 'WHALE_ALERT') {
          const notifyInfoText = 'WHALE ALERT 🐋';
          notifyInfo(notifyInfoText, () => {
            navigate(
              `${influencerId ? `/${influencerId}` : ''}/${MESSAGES}/${CHAT.replace(':chatId', data.userInfluencer?.id)}`,
            );
          });
          dispatch(setHasNewNotifications(true));
        } else {
          const notifyInfoText =
            user.role === CRM_ORGANIZATION_MEMBER_ROLE.CHATTER
              ? `An unlock has been made`
              : `New ${data.type} purchase from ${data.userInfluencer?.name || 'Unknown'} ($${formatPrice(data.cost)})`;
          notifyInfo(notifyInfoText, () => {
            navigate(
              `${influencerId ? `/${influencerId}` : ''}/${MESSAGES}/${CHAT.replace(':chatId', data.userInfluencer?.id)}`,
            );
          });
          dispatch(setHasNewNotifications(true));
        }
      }
    }
  };

  useSocketEvent(SOCKET_EVENTS.NOTIFICATION, handleNotification);

  const renderInfluencerNavigation = () => {
    return (
      <NavigationContainer>
        <NavigationItems>
          <LogoWrapper>
            <LogoComponent />
          </LogoWrapper>
          <NavigationItem>
            <NavLink to={HOME}>
              <Badge />
              <NavLinkIconWrapper>
                <HomeIcon />
              </NavLinkIconWrapper>
              <NavLinkText>Home</NavLinkText>
            </NavLink>
          </NavigationItem>
          <NavigationItem>
            <NavLink to={WALLET}>
              <Badge />
              <NavLinkIconWrapper>
                <WalletIcon />
              </NavLinkIconWrapper>
              <NavLinkText>Wallet</NavLinkText>
            </NavLink>
          </NavigationItem>
          {user.featureEnabled && (
            <NavigationItem>
              <NavLink to={REFERRALS}>
                <Badge />
                <NavLinkIconWrapper>
                  <ImageUserPlusIcon />
                </NavLinkIconWrapper>
                <NavLinkText>Referrals</NavLinkText>
              </NavLink>
            </NavigationItem>
          )}
          <NavigationItem>
            <NavLink to={PROFILE}>
              <Badge />
              <NavLinkIconWrapper>
                <UserIcon />
              </NavLinkIconWrapper>
              <NavLinkText>Profile</NavLinkText>
            </NavLink>
          </NavigationItem>
        </NavigationItems>
      </NavigationContainer>
    );
  };

  const renderAgencyNavigation = () => {
    return (
      <NavigationContainer>
        <NavigationItems>
          <LogoWrapper>
            <LogoComponent />
          </LogoWrapper>
          <NavigationItem>
            <NavLink to={AGENCY_HOME}>
              <Badge />
              <NavLinkIconWrapper>
                <HomeIcon />
              </NavLinkIconWrapper>
              <NavLinkText>Home</NavLinkText>
            </NavLink>
          </NavigationItem>
          <NavigationItem>
            <NavLink to={AGENCY_INFLUENCERS}>
              <Badge />
              <NavLinkIconWrapper>
                <UsersIcon />
              </NavLinkIconWrapper>
              <NavLinkText>Creators</NavLinkText>
            </NavLink>
          </NavigationItem>
          <NavigationItem>
            <NavLink to={AGENCY_SETTINGS}>
              <Badge />
              <NavLinkIconWrapper>
                <SettingsIcon />
              </NavLinkIconWrapper>
              <NavLinkText>Settings</NavLinkText>
            </NavLink>
          </NavigationItem>
        </NavigationItems>
      </NavigationContainer>
    );
  };

  const renderChatterNavigation = () => {
    return (
      <NavigationContainer>
        <NavigationItems>
          <LogoWrapper>
            <LogoComponent />
          </LogoWrapper>
          <NavigationItem>
            <NavLink to={CHATTER_HOME}>
              <Badge />
              <NavLinkIconWrapper>
                <ListIcon />
              </NavLinkIconWrapper>
              <NavLinkText>Creators</NavLinkText>
            </NavLink>
          </NavigationItem>
          {isOwnerOrTeamLead && (
            <NavigationItem>
              <NavLink to={ORGANIZATION_MEMBERS}>
                <Badge />
                <NavLinkIconWrapper>
                  <UserEditIcon />
                </NavLinkIconWrapper>
                <NavLinkText>Members</NavLinkText>
              </NavLink>
            </NavigationItem>
          )}
          {isOwnerOrTeamLead && (
            <NavigationItem>
              <NavLink to={ORGANIZATION_ANALYTICS}>
                <Badge />
                <NavLinkIconWrapper>
                  <AnalyticsIcon />
                </NavLinkIconWrapper>
                <NavLinkText>Analytics</NavLinkText>
              </NavLink>
            </NavigationItem>
          )}
          {canViewMessageReviews && (
            <NavigationItem>
              <NavLink to={MESSAGE_REVIEWS}>
                <Badge />
                <NavLinkIconWrapper>
                  <AnnotationAlertIcon />
                </NavLinkIconWrapper>
                <NavLinkText>Message Reviews</NavLinkText>
              </NavLink>
            </NavigationItem>
          )}
          <NavigationItem>
            <NavLink to={CHATTER_SETTINGS}>
              <Badge />
              <NavLinkIconWrapper>
                <SettingsIcon />
              </NavLinkIconWrapper>
              <NavLinkText>Settings</NavLinkText>
            </NavLink>
          </NavigationItem>
          {influencerId && !fetchInfluencerByIdPending && (
            <>
              {influencer && (
                <Row>
                  <Avatar src={influencer.profilePhoto} />
                  <Text15Bold>{influencer.name}</Text15Bold>
                </Row>
              )}
              {canViewMessages && (
                <NavigationItem>
                  <NavLink to={CHATTER_MESSAGES.replace(':influencerId', influencerId)}>
                    <Badge />
                    <NavLinkIconWrapper>
                      <MessageChatSquareIcon />
                    </NavLinkIconWrapper>
                    <NavLinkText>Messages</NavLinkText>
                  </NavLink>
                </NavigationItem>
              )}
              {isOwnerOrTeamLead && (
                <NavigationItem>
                  <NavLink to={CHATTER_COLLECTIONS.replace(':influencerId', influencerId)}>
                    <Badge />
                    <NavLinkIconWrapper>
                      <BookClosedIcon />
                    </NavLinkIconWrapper>
                    <NavLinkText>Collections</NavLinkText>
                  </NavLink>
                </NavigationItem>
              )}
              {canViewVault && (
                <NavigationItem>
                  <NavLink to={CHATTER_VAULT.replace(':influencerId', influencerId)}>
                    <Badge />
                    <NavLinkIconWrapper>
                      <SafeIcon />
                    </NavLinkIconWrapper>
                    <NavLinkText>Vault</NavLinkText>
                  </NavLink>
                </NavigationItem>
              )}
              {canViewNotification && (
                <NavigationItem>
                  <NavLink
                    to={CHATTER_NOTIFICATIONS.replace(':influencerId', influencerId)}
                    onClick={() => dispatch(setHasNewNotifications(false))}>
                    <Badge />
                    <NavLinkIconWrapper>
                      <BellIcon />
                    </NavLinkIconWrapper>
                    {newNotifications && <RedDot />}
                    <NavLinkText>Notifications</NavLinkText>
                  </NavLink>
                </NavigationItem>
              )}
              {canEditInfluencer && (
                <NavigationItem>
                  <NavLink to={CHATTER_EDIT.replace(':influencerId', influencerId)}>
                    <Badge />
                    <NavLinkIconWrapper>
                      <EditIcon />
                    </NavLinkIconWrapper>
                    <NavLinkText>Edit</NavLinkText>
                  </NavLink>
                </NavigationItem>
              )}
              {canPostStory && (
                <PostStoryBtn onClick={() => postStoryModalRef.current.show()}>
                  Post Story <AddIcon />
                </PostStoryBtn>
              )}
            </>
          )}
        </NavigationItems>
      </NavigationContainer>
    );
  };

  const renderContent = () => {
    switch (true) {
      case user.role === USER_ROLE.INFLUENCER:
        return renderInfluencerNavigation();
      case CRM_ORGANIZATION_MEMBER_ROLES.includes(user.role):
        return renderChatterNavigation();
      case user.role === USER_ROLE.AGENCY:
        return renderAgencyNavigation();
      default:
        break;
    }
  };

  return (
    <>
      <NavigationWrapper>{renderContent()}</NavigationWrapper>
      <PostStoryModal modalRef={postStoryModalRef} influencerId={influencerId} />
    </>
  );
};

export default Navigation;
